import {
  ESIGN_ACTIVITY_COPY,
  ESIGN_ACTIVITY_ERROR_COPY,
} from '@/constants/esignature';

/**
 * Pulls the `err_*` code out of whatever the esign stage-action endpoints reject
 * with.
 *
 * The ATS base query hands back `{ error: result.errors }`, so a 422 usually
 * arrives as a bare array of codes. A transport-level failure arrives as an
 * axios error whose body carries the same array. Both are read here, body
 * first, so axios' own "Request failed with status code 422" never wins over a
 * real code.
 */
export const esignActivityErrorCode = (error?: unknown): string | undefined => {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) {
    const first = error.find((entry) => typeof entry === 'string');
    return first as string | undefined;
  }

  const candidate = error as {
    message?: string;
    data?: { errors?: unknown[] };
    response?: { data?: { errors?: unknown[] } };
    errors?: unknown[];
  };
  const fromBody =
    candidate.response?.data?.errors?.[0] ??
    candidate.data?.errors?.[0] ??
    candidate.errors?.[0];
  if (typeof fromBody === 'string') return fromBody;
  return candidate.message;
};

/**
 * Display copy for a rejected save, validate, send or preview.
 *
 * Unrecognised values fall back to generic copy rather than being shown raw:
 * the server only emits `err_*` codes on these endpoints, but a network error
 * reaching this path would otherwise leak an internal message into a toast.
 */
export const esignActivityErrorCopy = (error?: unknown): string => {
  const code = esignActivityErrorCode(error);
  if (!code) return ESIGN_ACTIVITY_COPY.SAVE_ERROR;
  return ESIGN_ACTIVITY_ERROR_COPY[code] ?? ESIGN_ACTIVITY_COPY.SAVE_ERROR;
};
